import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api, formatApiError } from "@/lib/api";
import { Link } from "react-router-dom";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { ClipboardCheck, XCircle, CheckCircle2, UserCog } from "lucide-react";
import { toast } from "sonner";

const REJECT_REASONS = [
  "Photo missing or unclear",
  "Signature missing",
  "Area not fully cleaned",
  "Checklist item skipped",
  "Wrong area completed",
  "Needs rework",
];

export default function Reviews() {
  const qc = useQueryClient();
  const { data: tasks } = useQuery({
    queryKey: ["reviews"],
    queryFn: async () => (await api.get("/tasks", { params: { status: "completed" } })).data,
    refetchInterval: 10000,
  });
  const { data: users } = useQuery({
    queryKey: ["employees"],
    queryFn: async () => (await api.get("/employees")).data,
  });
  const { data: facilities } = useQuery({
    queryKey: ["facilities"],
    queryFn: async () => (await api.get("/facilities")).data,
  });
  const userMap = Object.fromEntries((users || []).map((u) => [u.id, u]));

  const [filterFac, setFilterFac] = useState("all");
  const filtered = (tasks || []).filter((t) => filterFac === "all" || t.facility_id === filterFac);

  const [rejectTask, setRejectTask] = useState(null);
  const [rejectReason, setRejectReason] = useState("");
  const [rejectNote, setRejectNote] = useState("");

  const [reassignTask, setReassignTask] = useState(null);
  const [reassignTo, setReassignTo] = useState("");

  const approve = async (t) => {
    try {
      await api.post(`/tasks/${t.id}/verify`, { approve: true });
      toast.success("Approved");
      qc.invalidateQueries({ queryKey: ["reviews"] });
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const submitReject = async () => {
    const reason = [rejectReason, rejectNote].filter(Boolean).join(" — ");
    if (!reason) { toast.error("Pick a reason or add a note"); return; }
    try {
      await api.post(`/tasks/${rejectTask.id}/verify`, { approve: false, reason });
      toast.success("Rejected — assignee notified");
      setRejectTask(null); setRejectReason(""); setRejectNote("");
      qc.invalidateQueries({ queryKey: ["reviews"] });
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const submitReassign = async () => {
    if (!reassignTo) { toast.error("Pick someone to reassign to"); return; }
    try {
      await api.patch(`/tasks/${reassignTask.id}`, { assigned_to: reassignTo, status: "assigned" });
      toast.success("Reassigned");
      setReassignTask(null); setReassignTo("");
      qc.invalidateQueries({ queryKey: ["reviews"] });
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="p-4 md:p-8 max-w-5xl">
      <div className="mb-6">
        <div className="label-caps text-muted-foreground mb-2">Supervisor</div>
        <h1 className="font-display text-3xl md:text-4xl font-black tracking-tight">Reviews</h1>
        <p className="text-sm text-muted-foreground mt-2">Tasks awaiting your approval. Approve, send back with a reason, or reassign to someone else.</p>
      </div>

      <div className="flex items-center gap-3 mb-5">
        <span className="text-xs text-muted-foreground">Facility</span>
        <Select value={filterFac} onValueChange={setFilterFac}>
          <SelectTrigger className="w-[220px]" data-testid="reviews-facility-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All facilities</SelectItem>
            {(facilities || []).map((f) => (
              <SelectItem key={f.id} value={f.id}>{f.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <div className="ml-auto text-xs text-muted-foreground">{filtered.length} pending</div>
      </div>

      <div className="space-y-3">
        {filtered.map((t) => {
          const u = userMap[t.completed_by];
          return (
            <div
              key={t.id}
              className="border border-border bg-card rounded-2xl p-4 md:p-5"
              data-testid={`review-${t.id}`}
            >
              <div className="flex items-start justify-between gap-4 flex-wrap">
                <Link to={`/app/tasks/${t.id}`} className="min-w-0 flex-1 group">
                  <div className="flex items-center gap-3">
                    <Avatar className="w-9 h-9 border border-border shrink-0">
                      <AvatarImage src={u?.photo_url} alt={u?.name} />
                      <AvatarFallback>{u?.name?.[0] || "?"}</AvatarFallback>
                    </Avatar>
                    <div className="min-w-0">
                      <div className="font-medium text-sm truncate group-hover:text-primary transition-colors">{t.title}</div>
                      <div className="text-xs text-muted-foreground mt-0.5 truncate">
                        {u?.name || "—"} · {new Date(t.completed_at).toLocaleString()}
                      </div>
                    </div>
                  </div>
                </Link>
                <div className="flex items-center gap-2 shrink-0">
                  <Badge variant="outline">Ready for review</Badge>
                  <Button
                    variant="ghost" size="sm" className="rounded-xl"
                    onClick={() => { setReassignTask(t); setReassignTo(""); }}
                    data-testid={`reassign-${t.id}`}
                  >
                    <UserCog className="w-4 h-4 mr-1" />Reassign
                  </Button>
                  <Button
                    variant="ghost" size="sm" className="rounded-xl text-destructive hover:text-destructive"
                    onClick={() => { setRejectTask(t); setRejectReason(""); setRejectNote(""); }}
                    data-testid={`reject-${t.id}`}
                  >
                    <XCircle className="w-4 h-4 mr-1" />Reject
                  </Button>
                  <Button
                    size="sm" className="rounded-xl"
                    onClick={() => approve(t)}
                    data-testid={`approve-${t.id}`}
                  >
                    <CheckCircle2 className="w-4 h-4 mr-1" />Approve
                  </Button>
                </div>
              </div>
            </div>
          );
        })}
        {filtered.length === 0 && (
          <div className="border border-dashed border-border p-12 text-center rounded-2xl">
            <ClipboardCheck className="w-8 h-8 text-muted-foreground mx-auto mb-3" />
            <div className="font-medium">No pending reviews</div>
            <p className="text-sm text-muted-foreground mt-1">All completed tasks are verified.</p>
          </div>
        )}
      </div>

      {/* Reject dialog */}
      <Dialog open={!!rejectTask} onOpenChange={(o) => !o && setRejectTask(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reject &amp; send back</DialogTitle>
            <DialogDescription>The assignee is notified and the task returns to their queue.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <span className="text-xs font-medium text-muted-foreground">Reason</span>
              <Select value={rejectReason} onValueChange={setRejectReason}>
                <SelectTrigger data-testid="reject-reason"><SelectValue placeholder="Pick a reason" /></SelectTrigger>
                <SelectContent>
                  {REJECT_REASONS.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <span className="text-xs font-medium text-muted-foreground">Note (optional)</span>
              <Textarea
                data-testid="reject-note"
                rows={3}
                value={rejectNote}
                onChange={(e) => setRejectNote(e.target.value)}
                placeholder="Anything else the assignee should know"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setRejectTask(null)}>Cancel</Button>
            <Button variant="destructive" onClick={submitReject} data-testid="reject-submit">Send back</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reassign dialog */}
      <Dialog open={!!reassignTask} onOpenChange={(o) => !o && setReassignTask(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reassign task</DialogTitle>
            <DialogDescription>Pick a new owner — they'll receive a notification and the task resets to assigned.</DialogDescription>
          </DialogHeader>
          <div className="py-2">
            <Select value={reassignTo} onValueChange={setReassignTo}>
              <SelectTrigger data-testid="reassign-to"><SelectValue placeholder="Pick a team member" /></SelectTrigger>
              <SelectContent>
                {(users || []).filter((u) => u.employment_status === "active" && u.id !== reassignTask?.completed_by).map((u) => (
                  <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setReassignTask(null)}>Cancel</Button>
            <Button onClick={submitReassign} data-testid="reassign-submit">Reassign</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
